import React, { useEffect, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import Swal from "sweetalert2";

interface Quisioner {
  id: number;
  pertanyaan: string;
  answers?: {
    user_id: string;
    user: string;
    nilai: string;
  }[];
}

interface VoteFormProps {
  presidenId: number;
  jabatan: string;
  nama: string;
}

const VoteForm: React.FC<VoteFormProps> = ({ presidenId, jabatan, nama }) => {
  const [quisioners, setQuisioners] = useState<Quisioner[]>([]);
  const [answers, setAnswers] = useState<{ [key: number]: number }>({});
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState<number | null>(null);

  // Fetch profile user login
  const fetchProfile = async () => {
    try {
      const token = Cookies.get("token");
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUserId(res.data?.id || null);
    } catch (err) {
      console.error("Gagal fetch profile:", err);
    }
  };

  // Ambil daftar quisioner + auto isi jika sudah dijawab
  const fetchQuisioners = async (profileId?: number) => {
    try {
      const token = Cookies.get("token");
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/presidens/${presidenId}/quisioners`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = res.data.quisioners || [];
      setQuisioners(data);

      // Auto isi jawaban kalau user sudah pernah menjawab
      if (profileId) {
        const prefilledAnswers: { [key: number]: number } = {};
        data.forEach((q: Quisioner) => {
          const userAnswer = q.answers?.find((a) => Number(a.user_id) === profileId);
          if (userAnswer) {
            prefilledAnswers[q.id] = Number(userAnswer.nilai);
          }
        });
        setAnswers(prefilledAnswers);
      }
    } catch (err) {
      console.error("Gagal fetch quisioner:", err);
    }
  };

  useEffect(() => {
    const init = async () => {
      await fetchProfile(); // Dapatkan user ID dulu
    };
    init();
  }, []);

  // Fetch quisioners setelah userId tersedia
  useEffect(() => {
    if (userId) {
      fetchQuisioners(userId);
    }
  }, [userId, presidenId]);

  const handleChange = (qId: number, nilai: number) => {
    setAnswers((prev) => ({ ...prev, [qId]: nilai }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const token = Cookies.get("token");

      const payload = {
        answers: Object.entries(answers).map(([qId, nilai]) => ({
          quisioner_id: Number(qId),
          nilai,
        })),
      };

      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/quisioners/${presidenId}/answer`, payload, { headers: { Authorization: `Bearer ${token}` } });

      Swal.fire({
        icon: "success",
        title: "Berhasil!",
        text: "Jawaban berhasil dikirim.",
        timer: 2000,
        showConfirmButton: false,
      });

      setAnswers({});
      setComment("");
    } catch (err) {
      console.error("Gagal kirim jawaban:", err);
      Swal.fire({
        icon: "error",
        title: "Oops!",
        text: "Gagal mengirim jawaban, coba lagi.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-yellow-50 p-6 rounded-lg shadow max-w-2xl mx-auto">
      <h2 className="text-xl font-bold mb-6 text-center">Quisioner Pejabat Publik Republik Indonesia</h2>

      {quisioners.map((q, idx) => (
        <div key={q.id} className="mb-6 border-b pb-4">
          <p className="mb-2 font-medium">
            {idx + 1}. {q.pertanyaan}
          </p>
          <div className="flex gap-4 items-center">
            {[1, 2, 3, 4, 5].map((n) => (
              <label key={n} className="flex items-center gap-1">
                <input type="radio" name={`q-${q.id}`} value={n} checked={answers[q.id] === n} onChange={() => handleChange(q.id, n)} />
                {n}
              </label>
            ))}
          </div>
        </div>
      ))}

      {/* Komentar tambahan */}
      <div className="mb-6">
        <label className="block mb-2 font-medium">Komentar tambahan (opsional)</label>
        <textarea className="w-full border rounded p-2" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Tuliskan pendapat Anda..."></textarea>
      </div>

      {/* Tombol submit */}
      <button disabled={loading} onClick={handleSubmit} className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-2 rounded font-semibold w-full">
        {loading ? "Mengirim..." : "Kirim Quisioner"}
      </button>
    </div>
  );
};

export default VoteForm;
